import { useEffect, useState } from "react";
import { FileDown, Send, Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, downloadBlob } from "../../lib/api";
import { Btn, Card } from "../kit/Kit";
import { AIProgress } from "../kit/AIProgress";
import { clienteFields, REVISION_GROUPS, creditoFields, missingIn } from "./fields";
import { dictamenCompleto } from "./StepDictamen";

export const StepResumen = ({ id, exp, setExp, c, cat, result, state, STEPS, goTo, ro }) => {
  const [memo, setMemo] = useState(null);
  const running = memo && ["queued", "running"].includes(memo.status);
  useEffect(() => {
    if (exp.memo_job_id) http.get(`/expedientes/${id}/jobs/${exp.memo_job_id}`).then((r) => setMemo(r.data)).catch(() => null);
  }, [id, exp.memo_job_id]);
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => http.get(`/expedientes/${id}/jobs/${memo.id}`).then((r) => setMemo(r.data)), 2000);
    return () => clearInterval(t);
  }, [running, id, memo?.id]);

  const faltantes = [
    ...missingIn(clienteFields(cat), c).map((f) => [0, f.l]),
    ...missingIn(REVISION_GROUPS.flatMap((g) => g[1]), c).map((f) => [2, f.l]),
    ...missingIn(creditoFields, c).map((f) => [3, f.l]),
    ...(result ? result.gates.filter((g) => g.status === "cond").map((g) => [4, `${g.name}: ${g.detail} — A CONFIRMAR`]) : []),
    ...(result ? result.gates.filter((g) => g.status === "fail").map((g) => [5, `${g.name}: no cumple (${g.detail})`]) : []),
    ...(dictamenCompleto(exp.dictamen) ? [] : [[6, "Dictamen: genera y revisa las 6 secciones (requerido para enviar)"]]),
  ];
  const gen = async () => {
    const r = await http.post(`/expedientes/${id}/memo`);
    setExp({ ...exp, memo_job_id: r.data.job_id });
    setMemo({ id: r.data.job_id, status: "queued", progress: 0, message: "En cola" });
    toast.success("Generando memo borrador…");
  };
  const submit = async () => {
    if (!window.confirm("¿Enviar el expediente a revisión del Director de Crédito? Quedará en solo lectura.")) return;
    try { const r = await http.post(`/expedientes/${id}/submit`); setExp(r.data); toast.success("Expediente enviado a revisión"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-12">
      <Card title="Verificación del expediente" subtitle={`${faltantes.length} pendientes (n.d. / A CONFIRMAR)`} className="lg:col-span-7" testid="resumen-checklist">
        <div className="mb-5 grid grid-cols-2 gap-2 sm:grid-cols-3">
          {STEPS.slice(0, 7).map((s, i) => (
            <button key={s} onClick={() => goTo(i)} className="flex items-center gap-2 rounded-xl bg-[#FAF8F5] px-3 py-2 text-left text-sm hover:bg-[#FDF0EB]" data-testid={`resumen-step-${i + 1}`}>
              {state[i] ? <CheckCircle2 size={15} className="text-emerald-600" /> : <AlertTriangle size={15} className="text-amber-500" />}{s}
            </button>
          ))}
        </div>
        <ul className="space-y-2">
          {faltantes.map(([st, t], i) => (
            <li key={i} className="flex items-start gap-2 rounded-xl border border-[#E2DDD5] px-3.5 py-2.5 text-sm" data-testid={`faltante-${i}`}>
              <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-500" /><span className="flex-1">{t}</span>
              <button onClick={() => goTo(st)} className="text-xs font-semibold text-[#E8643C] hover:underline">Ir al paso {st + 1}</button>
            </li>
          ))}
          {faltantes.length === 0 && <p className="text-sm text-emerald-700">Sin pendientes.</p>}
        </ul>
      </Card>
      <div className="space-y-6 lg:col-span-5">
        <Card title="Credit Memo borrador" subtitle="10 secciones + Anexo A con los datos confirmados del expediente." testid="resumen-memo">
          <Btn kind="primary" className="w-full" onClick={gen} disabled={running} data-testid="generar-memo-borrador-button">
            {running ? <Loader2 size={16} className="animate-spin" /> : <FileDown size={16} />} Generar memo borrador (PDF)
          </Btn>
          {memo && (
            <div className="mt-4" data-testid="memo-job-status">
              <AIProgress job={memo} title="Credit Memo (PDF)" compact testid="memo-progress" />
              {memo.status === "done" && (
                <Btn kind="accent" className="mt-3 w-full" data-testid="descargar-memo-borrador-button"
                  onClick={() => downloadBlob(`/expedientes/${id}/memo/${memo.id}/pdf`, `Credit_Memo_${exp.nombre.slice(0, 30)}.pdf`)}>
                  <FileDown size={16} /> Descargar PDF
                </Btn>
              )}
            </div>
          )}
        </Card>
        <Card title="Enviar a revisión" subtitle={`Crea la versión v${exp.version} con su PDF y la envía al Director de Crédito.`} testid="resumen-submit">
          <Btn kind="accent" className="w-full" disabled={ro || !dictamenCompleto(exp.dictamen)} onClick={submit} data-testid="enviar-revision-button"><Send size={16} /> {ro ? "Enviado a revisión" : "Enviar a revisión"}</Btn>
          {!ro && !dictamenCompleto(exp.dictamen) && <p className="mt-2 text-xs text-amber-700" data-testid="enviar-requiere-dictamen">Genera el dictamen en el paso 7 antes de enviar.</p>}
        </Card>
      </div>
    </div>
  );
};
